import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LOGO from "../../../assets/img/MOSIDA-logo.png";
import FifaBg from "../../../assets/img/fifa/fifa-inner.png";
import WorldCupCard1 from "../../../assets/img/fifa/world-cup-1.png";
import WorldCupCard2 from "../../../assets/img/fifa/world-cup-2.png";
import WorldCupCard3 from "../../../assets/img/fifa/world-cup-3.png";
import WorldCupCard4 from "../../../assets/img/fifa/world-card-4.png";
import {
  isFifaWorldCup2026Station,
  persistSelectedFifaCardDesign,
  readActiveStoredStation,
  resolveFifaStationSlug,
  type FifaCardDesignId,
} from "../../../utils/stationFlowStorage";
import { readSelectedAiSelfieArtFromStorage } from "../../../services/stylePromptResolver";
import {
  fifaCategoryIconUrl,
  fifaCategorySliderImageUrls,
  getActiveFifaCategories,
  persistSelectedFifaCategory,
  recordFifaCategoryUsage,
  type FifaCategory,
} from "../../../services/fifaCategoryService";
import Loader from "../../../compoment/ui/Loader";

const FIFA_CATEGORY_BORDER_CLASSES = [
  "selectFifaCardOption--green",
  "selectFifaCardOption--red",
  "selectFifaCardOption--pink",
  "selectFifaCardOption--gold",
  "selectFifaCardOption--blue",
] as const;

const MAX_VISIBLE_STACK = 4;
const STACK_STEP_X = 86;
const STACK_SCALE_STEP = 0.06;

function getStackLayout(stackPosition: number) {
  return {
    x: stackPosition * STACK_STEP_X,
    scale: 1 - stackPosition * STACK_SCALE_STEP,
    z: MAX_VISIBLE_STACK - stackPosition,
  };
}

const FALLBACK_PREVIEW_SLIDES = [
  [WorldCupCard1, WorldCupCard2, WorldCupCard3, WorldCupCard4],
] as const;

type FifaCardOption = {
  id: FifaCardDesignId;
  title: ReactNode;
  borderClass: string;
  previewSlides: readonly string[];
  cardScale: number;
};

function getFifaCategoryBorderClass(index: number): string {
  return FIFA_CATEGORY_BORDER_CLASSES[index % FIFA_CATEGORY_BORDER_CLASSES.length];
}

function buildPreviewSlides(category: FifaCategory, index: number): string[] {
  const sliderUrls = fifaCategorySliderImageUrls(category);
  if (sliderUrls.length > 0) return sliderUrls;

  const iconUrl = fifaCategoryIconUrl(category);
  if (iconUrl) return [iconUrl];

  const fallback =
    FALLBACK_PREVIEW_SLIDES[index % FALLBACK_PREVIEW_SLIDES.length] ||
    FALLBACK_PREVIEW_SLIDES[0];
  return [...fallback];
}

function mapFifaCategoryToCardOption(
  category: FifaCategory,
  index: number
): FifaCardOption {
  return {
    id: category.id,
    title: category.name,
    borderClass: getFifaCategoryBorderClass(index),
    previewSlides: buildPreviewSlides(category, index),
    cardScale: 1,
  };
}

function FifaCardStackSlider({
  slides,
  cardScale = 1,
}: {
  slides: readonly string[];
  cardScale?: number;
}) {
  const [activeIndex, setActiveIndex] = useState(0);

  const cardSlides = useMemo(
    () =>
      slides.filter(
        (url): url is string => typeof url === "string" && url.trim().length > 0
      ),
    [slides]
  );

  const visibleStack = useMemo(() => {
    const count = Math.min(MAX_VISIBLE_STACK, cardSlides.length);
    return Array.from({ length: count }, (_, stackPosition) => {
      const slideIndex = (activeIndex + stackPosition) % cardSlides.length;
      return {
        src: cardSlides[slideIndex],
        slideIndex,
        stackPosition,
      };
    });
  }, [activeIndex, cardSlides]);

  useEffect(() => {
    setActiveIndex(0);
  }, [cardSlides]);

  useEffect(() => {
    if (cardSlides.length <= 1) return;

    const timer = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % cardSlides.length);
    }, 3000);

    return () => window.clearInterval(timer);
  }, [cardSlides.length]);

  if (cardSlides.length === 0) return null;

  return (
    <div className="selectFifaCardSlider" aria-label="Card preview slider">
      {visibleStack.map(({ src, slideIndex, stackPosition }) => {
        const layout = getStackLayout(stackPosition);

        return (
          <button
            key={`${src}-${slideIndex}`}
            type="button"
            className="selectFifaCardSliderItem"
            style={{
              zIndex: layout.z,
              transform: `translate(-50%, -50%) translateX(${layout.x}px) scale(${1})`,
            }}
            onClick={() => setActiveIndex(slideIndex)}
            aria-label={`Show card ${slideIndex + 1} of ${cardSlides.length}`}
            aria-current={slideIndex === activeIndex}
          >
            <img src={src} alt="" draggable={false} />
          </button>
        );
      })}
    </div>
  );
}

const SelectFifaCard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const stationData = useMemo(() => readActiveStoredStation(), [location.state]);
  const selectedAiSelfieArt = useMemo(
    () => readSelectedAiSelfieArtFromStorage(),
    [location.state]
  );
  const isFifaFlow = isFifaWorldCup2026Station(stationData, selectedAiSelfieArt);
  const stationSlug = useMemo(
    () => resolveFifaStationSlug(stationData, selectedAiSelfieArt),
    [stationData, selectedAiSelfieArt]
  );
  const [fifaCategories, setFifaCategories] = useState<FifaCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);

  const fifaCardOptions = useMemo(
    () => fifaCategories.map((category, index) => mapFifaCategoryToCardOption(category, index)),
    [fifaCategories]
  );

  useEffect(() => {
    if (!isFifaFlow) return;

    let cancelled = false;

    (async () => {
      setCategoriesLoading(true);
      setCategoriesError(null);

      const res = await getActiveFifaCategories();
      if (cancelled) return;

      if (!res.success && res.data.length === 0) {
        setCategoriesError(res.error || res.message || "Could not load FIFA categories");
        setFifaCategories([]);
      } else {
        setFifaCategories(res.data.filter((category) => category.status !== false));
      }

      setCategoriesLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [isFifaFlow]);

  useEffect(() => {
    if (!isFifaFlow) {
      navigate(`/${stationSlug}/choose-option`, { replace: true });
    }
  }, [isFifaFlow, navigate, stationSlug]);

  const handleSelectCard = async (categoryId: FifaCardDesignId) => {
    const category = fifaCategories.find((item) => item.id === categoryId);
    if (category) {
      persistSelectedFifaCategory(category);
    }
    persistSelectedFifaCardDesign(categoryId);

    const usageResult = await recordFifaCategoryUsage(categoryId);
    console.log("usageResult", usageResult);
    if (!usageResult.success) {
      console.warn("[FIFA] Category usage tracking failed:", usageResult.error);
    }

    navigate(`/${stationSlug}/upload-fifa-image`);
  };

  if (!isFifaFlow) {
    return null;
  }

  return (
    <>
      {categoriesLoading && <Loader />}
      <section
        className="selectFifaCardSection"
        style={{ backgroundImage: `url(${FifaBg})` }}
      >
        <div className="selectFifaCardInner">
          <div className="mosidaLogoWhite selectFifaCardLogo">
            <img src={LOGO} alt="MOSIDA logo" className="logo" />
            <span className="badgeCreative">FIFA World Cup</span>
          </div>

          <div className="selectFifaCardBody">
            <h1 className="selectFifaCardHeading">
              <div className="selectFifaCardHeadingLine">
                SELECT{" "}
                <span className="selectFifaCardHeadingLine--gold">CARD DESIGN</span>
              </div>
            </h1>

            <div className="selectFifaCardList">
              {!categoriesLoading && fifaCardOptions.length === 0 ? (
                <div className="selectFifaCardEmpty" role="status" aria-live="polite">
                  <p className="selectFifaCardEmptyTitle">No card designs available</p>
                </div>
              ) : (
                fifaCardOptions.map((option) => (
                  <div
                    key={option.id}
                    className={`selectFifaCardOption ${option.borderClass}`}
                  >
                    <div className="selectFifaCardOptionTitle">{option.title}</div>
                    <div className="selectFifaCardPreview">
                      <FifaCardStackSlider
                        slides={option.previewSlides}
                        cardScale={option.cardScale}
                      />
                    </div>
                    <button
                      type="button"
                      className="selectFifaCardBtn mt-3"
                      onClick={() => handleSelectCard(option.id)}
                    >
                      SELECT
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SelectFifaCard;
